import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import { useCart } from '../context/CartContext';
import { Helmet } from 'react-helmet-async';
import { motion } from 'motion/react';
import { Anchor, Shield, Package, Compass, Play, ShoppingBag, ArrowRight } from 'lucide-react';

export default function EquipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const { products, resolveMediaUrl } = useBrand();
  const { addToCart, setIsCartOpen } = useCart();
  const navigate = useNavigate();
  
  if (id === 'endurance') {
    return <Navigate to="/expedition-cx2" replace />;
  }

  const equipment = products.find(p => p.id === id && p.category === 'Equipment');

  if (!equipment) {
    return <Navigate to="/equipment" replace />;
  }

  let features = [];
  if (equipment.blueprint) {
    try {
      features = JSON.parse(equipment.blueprint);
    } catch (e) {
      console.error("Failed to parse blueprint", e);
    }
  }

  const IconMap: Record<string, any> = {
    Shield, Anchor, Package, Compass, Play
  };

  return (
    <div className="pt-32 pb-24 bg-ows-white min-h-screen">
      <Helmet>
        <title>{`${equipment.name} | Open Water Syndicate`}</title>
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-24">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="aspect-square bg-ows-sand/20 rounded-[3rem] overflow-hidden flex items-center justify-center p-12 border border-ows-teal/5"
          >
            {equipment.image && (
              <img 
                src={resolveMediaUrl(equipment.image)} 
                alt={equipment.name} 
                className="w-full h-full object-contain mix-blend-multiply drop-shadow-2xl" 
                referrerPolicy="no-referrer"
              />
            )}
          </motion.div>
          
          <div className="space-y-8 pt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center gap-3 text-ows-teal mb-6">
                <Anchor className="h-6 w-6" />
                <span className="text-xs font-bold tracking-[0.4em] uppercase font-mono">Equipment Specs</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-light uppercase tracking-widest text-ows-dark mb-6">
                {equipment.name}
              </h1>
              <p className="text-ows-dark/60 text-lg leading-relaxed font-light mb-8">
                {equipment.description}
              </p>
              <div className="flex flex-wrap gap-4 items-center pt-4">
                <div className="p-8 bg-ows-dark text-white rounded-[2rem] border border-white/5 inline-block">
                  <div className="text-[8px] font-bold uppercase tracking-[0.5em] text-ows-teal mb-2 font-mono">Status</div>
                  <div className="text-xl font-display uppercase tracking-widest leading-none">
                    {equipment.name.toLowerCase().includes('c2') ? "Pre-Order" : (equipment.stock > 0 ? "Available" : "Waitlist")}
                  </div>
                </div>
                
                <button 
                  onClick={() => {
                    addToCart(equipment);
                    setIsCartOpen(true);
                  }}
                  className="px-10 py-8 bg-ows-orange text-white rounded-[2rem] font-bold text-[10px] tracking-[0.4em] uppercase hover:bg-ows-teal transition-all flex items-center gap-4 shadow-xl shadow-ows-orange/10 group"
                >
                  <ShoppingBag className="h-5 w-5" />
                  {equipment.name.toLowerCase().includes('c2') ? "Secure Pre-Order Spot" : "Add to Loadout"}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {equipment.name.toLowerCase().includes('c2') && (
                <div className="p-8 bg-ows-sand/30 rounded-3xl border border-ows-teal/5 max-w-md space-y-3">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-ows-dark/60">Pre-Order Logistics Protocol</p>
                  <p className="text-[10px] text-ows-dark/50 uppercase leading-relaxed font-mono">
                    We do not sell boards immediately. Your pre-order reserves a unit in Batch 001. A minimum order threshold of 200 boards system-wide is required to launch production. Delivery target is March 2027.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>

        {features.length > 0 && (
          <div className="mt-32">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 text-ows-teal mb-6">
                <Compass className="h-6 w-6" />
                <span className="text-xs font-bold tracking-[0.4em] uppercase font-mono">Technical Blueprint</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-light uppercase tracking-widest text-ows-dark">
                Anatomy
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature: any, index: number) => {
                const IconComp = IconMap[feature.icon] || Compass;
                return (
                <motion.div
                  key={feature.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-ows-sand/10 p-8 rounded-[2rem] border border-ows-teal/5 hover:bg-white hover:shadow-xl transition-all duration-500 group"
                >
                  <div className="h-12 w-12 bg-ows-sand flex items-center justify-center rounded-2xl mb-6 group-hover:bg-ows-teal group-hover:text-white transition-all">
                    <IconComp className="h-6 w-6 text-ows-teal group-hover:text-white" />
                  </div>
                  <h3 className="text-base font-bold tracking-[0.2em] uppercase text-ows-dark mb-3 group-hover:text-ows-teal transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-ows-dark/60 text-sm tracking-wide leading-relaxed font-light">
                    {feature.description}
                  </p>
                </motion.div>
              )})}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
